/**
 * Constantes de permisos del sistema SLYRSA.
 *
 * Cuando el back cambie alguna clave, solo hay que actualizar ESTE archivo.
 * Todos los componentes y el Sidebar importan desde aquí.
 *
 * Nomenclatura del backend: <modulo>.<area>.<recurso>.<accion>
 */

// ─── Almacén ──────────────────────────────────────────────────────────────────

export const ALMACEN = {
  // Herramientas
  // "ver" es ver TODAS las herramientas; "ver_asignadas" limita el listado a
  // las del propio usuario. El back acepta cualquiera de las dos para entrar al
  // módulo, así que las pantallas deben checar ambas.
  HERRAMIENTAS_VER:           'almacen.gestionar.herramientas.ver',
  HERRAMIENTAS_VER_ASIGNADAS: 'almacen.gestionar.herramientas.ver_asignadas',
  HERRAMIENTAS_CREAR:    'almacen.gestionar.herramientas.crear',
  HERRAMIENTAS_EDITAR:   'almacen.gestionar.herramientas.editar',
  HERRAMIENTAS_ELIMINAR: 'almacen.gestionar.herramientas.eliminar',
  HERRAMIENTAS_DAR_BAJA: 'almacen.gestionar.herramientas.dar_baja',

  // Movimientos
  MOVIMIENTOS_ASIGNAR:          'almacen.gestionar.movimientos.asignar',
  MOVIMIENTOS_REASIGNAR:        'almacen.gestionar.movimientos.reasignar',
  MOVIMIENTOS_DEVOLVER:         'almacen.gestionar.movimientos.devolver',
  MOVIMIENTOS_REASIGNAR_ALMACEN:'almacen.gestionar.movimientos.reasignar_almacen',

  // Mantenimiento
  MANTENIMIENTO_REGISTRAR: 'almacen.gestionar.mantenimiento.registrar',

  // Reportes
  REPORTES_VER:    'almacen.gestionar.reportes.ver',
  REPORTES_CREAR:  'almacen.gestionar.reportes.crear',
  REPORTES_CERRAR: 'almacen.gestionar.reportes.cerrar',

  // Historial
  HISTORIAL_VER: 'almacen.gestionar.historial.ver',

  // Requisiciones
  REQUISICIONES_VER:   'almacen.gestionar.requisiciones.ver',
  REQUISICIONES_CREAR: 'almacen.gestionar.requisiciones.crear',

  // Dashboard
  DASHBOARD_VER: 'almacen.inicio.dashboard.ver',

  // Prefijos usados en el Sidebar para el substring-match. Solo quedan los
  // recursos sin un permiso "ver" propio (Movimientos, Mantenimiento): su
  // visibilidad depende de tener cualquier accion, no de un ver dedicado.
  _PREFIX:            'almacen.gestionar',
  _PREFIX_MOVIMIENTOS:'almacen.gestionar.movimientos',
  _PREFIX_MANT:       'almacen.gestionar.mantenimiento',
} as const;

// ─── RH ───────────────────────────────────────────────────────────────────────

export const RH = {
  SOLICITUDES_PERSONAL_READ:     'rh.solicitudes_personal.base.read',
  SOLICITUDES_PERSONAL_CREATE:   'rh.solicitudes_personal.base.create',
  /**
   * «Eliminar solicitudes» en la pantalla de roles. No hay un permiso propio de
   * rechazar: esta es la clave que lo habilita (y la de reactivar).
   */
  SOLICITUDES_PERSONAL_DELETE:   'rh.solicitudes_personal.base.delete',
  /**
   * Aprobar y asignar salarios fuera del área propia.
   *
   * Tienen clave propia desde que se separaron del catálogo de áreas: antes
   * quien podía editar ese catálogo aprobaba solicitudes de toda la empresa.
   * No vienen en ningún rol; se otorgan a quien de verdad lo necesita.
   */
  SOLICITUDES_APROBAR_TODAS_LAS_AREAS:
    'rh.solicitudes_personal.especial.aprobar_todas_las_areas',
  SOLICITUDES_ASIGNAR_SALARIOS_TODAS_LAS_AREAS:
    'rh.solicitudes_personal.especial.asignar_salarios_todas_las_areas',

  GESTION_SOLICITUDES_READ:      'rh.gestion_solicitudes.base.read',

  RECLUTAMIENTO_CANDIDATOS_READ: 'rh.reclutamiento_candidatos.base.read',
  /** Alta manual desde el tablero. No viene en ningún rol: se otorga uno por uno. */
  RECLUTAMIENTO_ALTA_MANUAL:     'rh.reclutamiento_candidatos.especial.alta_manual',
  RECLUTAMIENTO_CERRAR_SELECCION: 'rh.reclutamiento_candidatos.especial.cerrar_seleccion',
  RECLUTAMIENTO_CONSULTAR_APROBADOS: 'rh.reclutamiento_candidatos.especial.consultar_aprobados',
  RECLUTAMIENTO_DECIDIR_APROBADOS: 'rh.reclutamiento_candidatos.especial.decidir_aprobados',

  ENTREVISTAS_READ:              'rh.entrevistas.base.read',
  ENTREVISTAS_CREATE:            'rh.entrevistas.base.create',

  EMPLEADOS_EXPEDIENTES_READ:    'rh.empleados_expedientes.base.read',
  EMPLEADOS_PREREGISTRO_READ:    'rh.empleados_preregistro.base.read',

  EVALUACIONES_READ:             'rh.evaluaciones.base.read',
  EVALUACIONES_CREATE:           'rh.evaluaciones.base.create',

  COMPRAS_READ:                  'rh.compras.base.read',
} as const;

// ─── Proveedores ──────────────────────────────────────────────────────────────

export const PROVEEDORES = {
  DASHBOARD_CONSULTAR:       'proveedores.inicio.dashboard.consultar',
  PROVEEDORES_CONSULTAR:     'proveedores.administracion.proveedores.consultar',
  ORDENES_COMPRA_CONSULTAR:  'proveedores.administracion.ordenes_compra.consultar',
  VALIDACIONES_CONSULTAR:    'proveedores.fiscal.validaciones.consultar',
} as const;

// ─── Generales / Catálogos ────────────────────────────────────────────────────

export const GENERALES = {
  CATALOGOS_PROYECTOS_READ: 'generales.catalogos.proyectos.read',
  CATALOGOS_ROLES_READ:     'generales.catalogos.roles.read',
  CATALOGOS_AREAS_READ:     'generales.catalogos.areas.read',
  CATALOGOS_PUESTOS_READ:   'generales.catalogos.puestos.read',
} as const;

// ─── Re-exports con alias legacy (para no romper imports existentes) ──────────

/** @deprecated Usa ALMACEN en su lugar */
export const ALMACEN_PERMISSIONS = {
  HERRAMIENTAS_VER:           ALMACEN.HERRAMIENTAS_VER,
  HERRAMIENTAS_VER_ASIGNADAS: ALMACEN.HERRAMIENTAS_VER_ASIGNADAS,
  HERRAMIENTAS_CREAR:    ALMACEN.HERRAMIENTAS_CREAR,
  HERRAMIENTAS_EDITAR:   ALMACEN.HERRAMIENTAS_EDITAR,
  HERRAMIENTAS_ELIMINAR: ALMACEN.HERRAMIENTAS_ELIMINAR,
  HERRAMIENTAS_DAR_BAJA: ALMACEN.HERRAMIENTAS_DAR_BAJA,
  MOVIMIENTOS_ASIGNAR:   ALMACEN.MOVIMIENTOS_ASIGNAR,
  MOVIMIENTOS_REASIGNAR: ALMACEN.MOVIMIENTOS_REASIGNAR,
  MOVIMIENTOS_DEVOLVER:  ALMACEN.MOVIMIENTOS_DEVOLVER,
  MANTENIMIENTO_REGISTRAR: ALMACEN.MANTENIMIENTO_REGISTRAR,
  REPORTES_VER:    ALMACEN.REPORTES_VER,
  REPORTES_CREAR:  ALMACEN.REPORTES_CREAR,
  REPORTES_CERRAR: ALMACEN.REPORTES_CERRAR,
  HISTORIAL_VER:   ALMACEN.HISTORIAL_VER,
} as const;
